import React, { useState } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
} from '@mui/material';
import { API } from './config';

const ResetPassword = () => {
  const { token } = useParams();
  const history = useHistory();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    if (password.length < 8) {
      setStatus({
        type: 'error',
        message: 'Password must be at least 8 characters.',
      });
      return;
    }

    if (password !== confirmPassword) {
      setStatus({
        type: 'error',
        message: 'Passwords do not match.',
      });
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API}/api/auth/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setStatus({
          type: 'error',
          message: data.message || 'Password reset failed. Please request a new reset link.',
        });
        return;
      }

      setStatus({
        type: 'success',
        message: 'Password updated successfully. Please sign in.',
      });

      setPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        history.push('/customer/sign-in');
      }, 1500);
    } catch (err) {
      console.error('Reset password error:', err);
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f7fb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={4} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
            <Typography
              variant="h4"
              align="center"
              fontWeight={700}
              gutterBottom
            >
              HaggleFreight
            </Typography>

            <Typography
              variant="h5"
              align="center"
              fontWeight={600}
              gutterBottom
            >
              Reset Password
            </Typography>

            <Typography
              variant="body2"
              align="center"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              Enter your new password below. This reset link expires after 30 minutes.
            </Typography>

            {status.message && (
              <Alert severity={status.type} sx={{ mb: 2 }}>
                {status.message}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                label="New Password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <TextField
                label="Confirm New Password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
                sx={{ mt: 3, py: 1.4, fontWeight: 700 }}
              >
                {loading ? 'Updating Password...' : 'Update Password'}
              </Button>
            </form>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                component={Link}
                to="/customer/sign-in"
                variant="text"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                Back to Customer Sign In
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                component={Link}
                to="/courier/sign-in"
                variant="text"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                Back to Courier Sign In
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                component={Link}
                to="/"
                variant="text"
                color="inherit"
                sx={{ fontWeight: 500 }}
              >
                Back to Home
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ResetPassword;
